<template>
  <ModalFullScreen
    v-model="open"
    fullscreen
    no-click-animation
    persistent>
    <ComponentLayout
      id="multiple-offer-different-price"
      fluid_main_container
      main_color="var(--color-main-green)">
      <template #breadcrumbs>
        <TenderBreadcrumbs
          :current="$t('Tenders.ToRequestNewPrice')"
          :dialog.sync="open"/>
      </template>
      <template #main_content>
        <div
          class="pr-8 mt-4 width-100">
          <div class="flex flex-between">
            <div class="text-left h1-headline">
              {{ $t('Tenders.ToRequestNewPrice') }} {{ $t('Common.For') }}
            </div>
            <SendOfferDifferentPrice
              :btn_text="$t('Tenders.SendPriceRequest')"
              :check-validation="checkValidation"
              class="pr-5"
              :disabled="send_disabled"
              :rows="send_model"
              :tender-id="tender.tenderId"
              @send="close"/>
          </div>
          <div class="pt-5">
            <sForm
              ref="form"
              :model="{items: lots}"
              :rules="rules">
              <div
                v-for="(lot, ind) in lots"
                :key="ind"
                :class="{'pb-5': ind != lots.length - 1}">
                <sAlert
                  class="px-4 py-4"
                  data-qa="lot-title"
                  small>
                  # {{ ind + 1 }} {{ lot.title }}
                </sAlert>
                <OfferDifferentPriceRadio
                  ref="setNewPrice"
                  class="px-3 py-2"
                  :currency="getCurrency(lot)"
                  :error.sync="lot.value_error"
                  :form-item="`items[${ind}].new_price`"
                  is-multiple
                  :is-sale="isSale"
                  :model_radio.sync="lot.new_price_mode"
                  :model_text.sync="lot.new_price"
                  @changePrice="(text, mode) => changePrice(lot, text, mode)"/>
                <v-data-table
                  :key="lot.new_price + lot.new_price_mode"
                  calculate-widths
                  class="offers-table offers "
                  :data-qa="lot.id + '-offers-table'"
                  disable-pagination
                  :headers="headers"
                  hide-default-footer
                  item-key="index"
                  :items="lot.items"
                  :no-data-text="$t('Tenders.NoData')">
                  <template #[`item.name`]="{ item }">
                    <VerificationStatus
                      v-if="item.bidderInfo && item.bidderInfo.verificationStatus == 'VERIFIED'"
                      class="mr-1"
                      :status="item.bidderInfo.verificationDecision"/>
                    <span> {{ item.bidderInfo ? item.bidderInfo.organizationName || '–' : '–' }} </span>
                    <div
                      v-if="item.bidderInfo && item.bidderInfo.rating != undefined "
                      class="opacity-6">
                      {{ $t('Tenders.Rating') }}: {{ item.bidderInfo.rating }}
                    </div>
                  </template>
                  <template #[`item.quantity`]="{ item }">
                    {{ item.selectedQuantity || item.quantity }} {{ (item.unit ? item.unit.title : '–') || '–' }}
                  </template>
                  <template #[`item.current_price`]="{ item }">
                    <ShowValueRoundingDigit
                      data-qa="current-price-value"
                      :minimum-fraction-digits="2"
                      :value="item.currencyPrice"/> {{ getCurrency(item) }}
                  </template>
                  <template #[`item.new_price`]="{ item, index }">
                    <sFormItem
                      v-if="(item.new_price || item.difference) && ( !item.edit_price || (lot.new_price_mode == 1 || lot.new_price_mode == 2))"
                      ref="input"
                      class="mb-0 width-100"
                      :prop="`items[${ind}].items[${index}].difference`">
                      <div class="flex gap-5 align-center">
                        <div class="h7-text-bold">
                          <ShowValueRoundingDigit
                            data-qa="new-price-value"
                            :minimum-fraction-digits="2"
                            :value="item.new_price"/>
                        </div>
                        <sButton
                          v-if="lot.new_price_mode == 'PRICE' || lot.new_price_mode == 'PERSENT'"
                          icon="edit"
                          small
                          type="text"
                          @click.stop="item.edit_price = true"/>
                      </div>
                    </sFormItem>
                    <template v-else>
                      <OfferDifferentPriceInput
                        ref="bid_input"
                        :currency="getCurrency(item)"
                        :form-item="`items[${ind}].items[${index}].difference`"
                        :type.sync="lot.new_price_mode"
                        :value.sync="item.input"
                        @blur="text => closeEditing(text, item, lot.new_price_mode)"
                        @change="text => changeBidPrice(item, text, lot.new_price_mode )"/>
                    </template>
                  </template>
                  <template #[`item.new_total`]="{ item, index }">
                    <div class="flex flex-between align-center">
                      <ShowValueRoundingDigit
                        data-qa="new-total-value"
                        :maximum-fraction-digits="2"
                        :minimum-fraction-digits="2"
                        :value="(item.selectedQuantity || item.quantity) * item.new_price"/> {{ getCurrency(item) }}
                      <sButton
                        icon="cross"
                        type="text"
                        @click="deleteItem(item, index, lot)"/>
                    </div>
                  </template>
                  <template #[`item.comment`]="{ item }">
                    <template v-if="item.comment && !item.edit_comment">
                      <span class="h7-text-bold">
                        {{ item.comment }}
                      </span>
                      <sButton
                        icon="edit"
                        small
                        type="text"
                        @click.stop="item.edit_comment = true"/>
                    </template>
                    <template v-else>
                      <sInput
                        clearable
                        data-qa="lot-comment"
                        :max-length="254"
                        :placeholder="$t('Common.OptionalComment')"
                        :value.sync="item.comment"
                        @blur="item.comment ? item.edit_comment = false : ''"
                        @keydown_enter="item.comment ? item.edit_comment = false : ''"
                        @keydown_esc="item.comment ? item.edit_comment = false : ''"/>
                    </template>
                  </template>
                </v-data-table>
              </div>
            </sForm>
          </div>
        </div>
      </template>
      <template #footer>
        <BackToTenderBtn
          key="footer"
          class="ml-auto"
          @click="close"/>
      </template>
    </ComponentLayout>
  </ModalFullScreen>
</template>

<script>
import ComponentLayout from 'pages/templates/ComponentLayout.vue'
import BackToTenderBtn from 'components/tenders/BackToTenderBtn.vue'
import TenderBreadcrumbs from 'components/tenders/TenderBreadcrumbs.vue'
import VerificationStatus from 'components/tenders/VerificationStatus.vue'
import ShowValueRoundingDigit from 'components/common/blocks/ShowValueRoundingDigit.vue'
import SendOfferDifferentPrice from 'components/tenders/tender page/offer different price/SendOfferDifferentPrice.vue'
import OfferDifferentPriceRadio from 'components/tenders/tender page/offer different price/OfferDifferentPriceRadio.vue'
import OfferDifferentPriceInput from 'components/tenders/tender page/offer different price/OfferDifferentPriceInput.vue'

import ModalFullScreen from 'components/common/elements/ModalFullScreen.vue'

import { modalMixin } from 'mixins'

export default {
  name: 'multipleOfferDifferentPrice',
  mixins: [ modalMixin],
  data() {
    return {
      rules: {},
      currencies: [],
      lots: []
    }
  },
  props: {
    dialog: Boolean,
    tender: Object,
    elements: Array,
    validateInput: Function,
    getNewPrice: Function,
    isSale: Boolean
  },
  async created() {
    await this.$store.dispatch('setCurrencies')
      .then(res => this.currencies = res)
  },
  computed: {
    open: {
      get() {
        return this.dialog
      },
      set(value) {
        if ( !value ) {
          this.$emit('close')
        }
        this.$emit('update:dialog', value)
      }
    },
    headers() {
      return [
        { text: this.$t('Tenders.Supplier') , value: 'name', },
        { text: this.$t('Catalog.Amount'), value: 'quantity', width: '15%' , sortable: false},
        { text: this.$t('Tenders.CurrentSupplierPrice'), value: 'current_price', width: '15%', sortable: false, class: 'header_break-spaces' },
        { text: this.$t('Tenders.NewPrice'), value: 'new_price', width: '15%', sortable: false },
        { text: this.$t('Suppliers.Comment'), value: 'comment', width: '20%', sortable: false },
        { text: this.$t('Tenders.NewTtotal'), value: 'new_total', width: '15%', sortable: false },
      ]
    },
    send_disabled() {
      return this.lots.some(lot => lot.value_error || lot.items.some(el => !el.new_price)) && this.$t('Tenders.NewPriceForAllSelectedOffers')
    },
    send_model() {
      let items = []
      this.lots.forEach(lot => {
        items.push(...lot.items.map(bid => ({
          bidItemId: bid.id ,
          price: bid.new_price,
          bidder: bid.org_num,
          comment: bid.comment
        })) )
      })
      return items
    }
  },
  methods: {
    close() {
      this.open = false
      this.lots = []
      this.$emit('close')
    },
    setItems() {
      this.setLots()
    },
    setRules() {
      const that = this
      let getRule = (type, required = true, lotId, newPriceMode, priceObject) => {
        return {
          [type]: {
            trigger: 'change',
            validator: (rule, value, callback) => {
              return that.validateInput(rule, priceObject, callback, newPriceMode, required, lotId)
            },
          },
        } 
      }
      let getlotsRules = (lot,index) => {
        let pref = `items[${index}]`
        const newPriceMode = lot.new_price_mode
        let lot_rules = {}//getRule(`${pref}.new_price`, index, false, lot?.items[0]?.lotId)
        lot.items.forEach((bid, ind) => {
          const budRuleName = `${pref}.items[${ind}].difference`

          const priceObject = {
            price: bid.new_price,
            percent: newPriceMode === 'PRICE' ? null : bid.input
          }

          lot_rules = {
            ...lot_rules,
            ...getRule(budRuleName, true, bid.lotId, newPriceMode, priceObject)
          }
        })
        return lot_rules
      }

      this.lots.forEach((lot, index) => {
        this.rules = {
          ...this.rules,
          ...getlotsRules(lot, index)
        }
      })
    },
    setLots() {
      let lots_obj = this.$_.groupBy(this.elements, 'lotId' )
      this.lots = Object.entries( lots_obj ).map(([id, items]) => {
        let curr = this.$_.groupBy(items, 'currencyId')
        curr = Object.keys( curr )?.length > 1 ? '' : Object.keys( curr )[0]
        return {
          value_error: '',
          new_price: null,
          new_price_mode: 'PRICE',
          currencyId: curr,
          items: items.map(el => ({
            ...el,
            new_price: '',
            difference: '',
            edit_price: true,
            edit_comment: true,
            comment: '',
            input: ''
          })),
          id,
          title: items[0]?.lotTitle
        }
      })
    },
    deleteItem(item, ind, lot) {
      let index = this.elements.findIndex( el => el.id == item.id)
      // eslint-disable-next-line vue/no-mutating-props
      this.elements.splice(index, 1)

      if ( !this.elements?.length ) {
        this.close()
      } else {
        lot.items.splice(ind, 1)
        this.lots = this.lots.filter(el => el.items?.length)
      }

    },
    changeBidPrice(item, text, mode, edit_price) {
      if (mode == 'PRICE') {
        item.new_price = text
        item.difference = text
        if (text && edit_price) {
          item.edit_price = false
        }
      } else if (text) {
        item.new_price = this.getNewPrice(item.currencyPrice, text, mode)
        item.difference = text
        if (edit_price) {
          item.edit_price = false
        }
      } else {
        item.new_price = null
        item.difference = null
      }
      item.input = text
    },
    changePrice(lot, text, mode) {
      lot.items.forEach(element => {
        this.changeBidPrice(element, text , mode, true)
      })
    },
    closeEditing(text, item, mode) {
      this.changeBidPrice(item, text, mode)
      if (text) {
        item.edit_price = false
      }
    },
    async checkValidation() {
      await this.setRules()

      let res = await this.$refs.form.validate().catch(() => {
        if (this.isSale) {
          return res
        }
        let has_persent_error = this.lots.some(el => {
          return el?.new_price_mode == 'PERSENT' && (el.new_price > 99.99 || el.items.some(it => it.new_price > 99.99))
        })
        if ( has_persent_error ) {
          this.$notice.info({
            title: '',
            message: this.$t('Tenders.TheDiscountCannotBeMoreThan99'),
            showClose: true,
            progress: true,
            duration: 20
          })
        }
      })
      return res
    },
  },
  components: {
    ModalFullScreen,
    BackToTenderBtn,
    ComponentLayout,
    TenderBreadcrumbs,
    VerificationStatus,
    ShowValueRoundingDigit,
    SendOfferDifferentPrice,
    OfferDifferentPriceInput,
    OfferDifferentPriceRadio
  }
}
</script>
